import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  isTransitioning: boolean;
  transitioningTo: Theme | null; // The theme we're transitioning TO
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem('parlayground-theme') as Theme;
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitioningTo, setTransitioningTo] = useState<Theme | null>(null);

  const isDark = theme === 'dark';

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    localStorage.setItem('parlayground-theme', theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const stored = localStorage.getItem('parlayground-theme');
      if (!stored) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    const targetTheme = theme === 'dark' ? 'light' : 'dark';

    // Start transition - set target immediately so overlay knows what to show
    setTransitioningTo(targetTheme);
    setIsTransitioning(true);

    // Change the actual theme partway through the animation
    setTimeout(() => {
      setThemeState(targetTheme);
    }, 400);

    // End transition after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
      setTransitioningTo(null);
    }, 1200);
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    if (newTheme === theme) return;

    setTransitioningTo(newTheme);
    setIsTransitioning(true);
    setTimeout(() => {
      setThemeState(newTheme);
    }, 400);
    setTimeout(() => {
      setIsTransitioning(false);
      setTransitioningTo(null);
    }, 1200);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, isDark, isTransitioning, transitioningTo, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
